import type { StorageAdapter } from "./storage";
import type { ConsentSnapshot } from "./types";

export const PROSPIX_OPTOUT_KEY = "prospix_optout";

/** Persist the local stop synchronously before any asynchronous revocation. */
export function markDurableOptout(storage: StorageAdapter) {
  storage.set(PROSPIX_OPTOUT_KEY, "1");
  return storage.get(PROSPIX_OPTOUT_KEY) === "1";
}

export function clearDurableOptout(storage: StorageAdapter) {
  storage.remove(PROSPIX_OPTOUT_KEY);
}

export function hasDurableOptout(storage: StorageAdapter) {
  return storage.get(PROSPIX_OPTOUT_KEY) === "1";
}

export type QueuedConsentPayload = {
  state: ConsentSnapshot["state"];
  cmp?: ConsentSnapshot["cmp"];
  /** Present only for a fresh in-page user decision, never initial state. */
  transitionVersion?: 1;
  occurredAt?: number;
  tcString?: string;
  uspString?: string;
  gppString?: string;
  gppSectionIds?: number[];
};

export type QueuedPrivacyBootstrap = {
  consent?: QueuedConsentPayload;
  optout?: boolean;
  revocationOccurred: boolean;
  revocationConsent?: QueuedConsentPayload;
  consumedIndexes: ReadonlySet<number>;
};

function queuedCall(entry: unknown) {
  if (Array.isArray(entry)) {
    return {
      method: typeof entry[0] === "string" ? entry[0] : undefined,
      args: entry.slice(1),
    };
  }
  if (entry && typeof entry === "object" && "method" in entry) {
    const candidate = entry as { method?: unknown; args?: unknown };
    return {
      method:
        typeof candidate.method === "string" ? candidate.method : undefined,
      args: Array.isArray(candidate.args) ? candidate.args : [],
    };
  }
  return { method: undefined, args: [] as unknown[] };
}

function isConsentState(value: unknown): value is ConsentSnapshot["state"] {
  return value === "unknown" || value === "granted" || value === "denied";
}

export function resolveQueuedInitOverride(entries: readonly unknown[]) {
  for (const entry of entries) {
    const call = queuedCall(entry);
    if (call.method !== "init") continue;
    const override = call.args[0];
    if (override && typeof override === "object" && !Array.isArray(override)) {
      return override as Record<string, unknown>;
    }
  }
  return undefined;
}

/**
 * Resolve privacy commands before runtime initialization. Loader queues are
 * replayed only after plugin startup, so without this pre-scan a queued denial
 * could arrive after a first-party identifier or analytics plugin had already
 * been initialized.
 */
export function resolveQueuedPrivacyBootstrap(
  entries: readonly unknown[],
): QueuedPrivacyBootstrap {
  let consent: QueuedConsentPayload | undefined;
  let optout: boolean | undefined;
  let revocationOccurred = false;
  let revocationConsent: QueuedConsentPayload | undefined;
  const consumedIndexes = new Set<number>();

  entries.forEach((entry, index) => {
    const call = queuedCall(entry);
    if (call.method === "optout" && typeof call.args[0] === "boolean") {
      optout = call.args[0];
      if (optout) {
        consent = { state: "denied" };
        revocationOccurred = true;
        revocationConsent = consent;
      }
      consumedIndexes.add(index);
      return;
    }
    if (call.method !== "consent") return;
    const payload = call.args[0];
    if (!payload || typeof payload !== "object") return;
    const candidate = payload as QueuedConsentPayload;
    if (!isConsentState(candidate.state)) return;
    consent = candidate;
    if (candidate.state === "denied") {
      revocationOccurred = true;
      revocationConsent = candidate;
    }
    consumedIndexes.add(index);
  });

  return {
    consent,
    optout,
    revocationOccurred,
    revocationConsent,
    consumedIndexes,
  };
}
